import React, { useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import { api } from "../../api/axios";
import { tableDateFormate } from "../../utils/dateHelper";
import { Trash, Image, Eye, EyeOff, Search, X, Crown, Ban, Download, UserX, UserCheck } from "lucide-react";
import { toast } from "react-toastify";
import MainLayout from "../../layout/MainLayout";
import { fileUlr } from "../../api/url";
import { toCSV, download } from "../../utils/csv";

/* -----------------------------
   Utils: build query params
   skips empty/null/undefined
   ----------------------------- */
const buildQueryParams = (obj) => {
    const params = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params.append(k, String(v));
    });
    return params;
};

const useDebounce = (value, delay = 300) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};


/* -----------------------------
   Sort Icon (better UI)
   ----------------------------- */
const SortIcon = ({ active, order }) => {
    if (!active) return <span className="ml-2 text-gray-300">↕</span>;
    if (order === "asc") return <span className="ml-2 text-purple-600">▲</span>;
    return <span className="ml-2 text-purple-600">▼</span>;
};

/* -----------------------------
   Responsive date hook
   returns boolean isSmall
   ----------------------------- */
const useIsSmall = () => {
    const [isSmall, setIsSmall] = useState(
        typeof window !== "undefined" ? window.innerWidth < 640 : false
    );

    useEffect(() => {
        const onResize = () => setIsSmall(window.innerWidth < 640);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return isSmall;
};

/* -----------------------------
   User More Details Modal
   dummy stats shown if missing
   ----------------------------- */
const UserMoreDetails = ({ user, onClose }) => {
    const [detailedUser, setDetailedUser] = useState(user);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const response = await api.get(`/users/${user._id}`);
                if (response.status === 200) {
                    setDetailedUser(response.data.user);
                }
            } catch (error) {
                console.error("Error fetching user details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (user._id) {
            fetchUserDetails();
        }
    }, [user._id]);


    // dummy fallbacks
    const totalPosts = detailedUser.totalPosts ?? 0;
    const followersCount = detailedUser.followersCount ?? 0;
    const followingCount = detailedUser.followingCount ?? 0;
    const remainingPhotoLimit = detailedUser.remainingPhotoLimit ?? 0;
    const remainingPostLimit = detailedUser.remainingPostLimit ?? 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative animate-fadeIn">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-black"
                    aria-label="Close"
                >
                    <X size={20} />
                </button>

                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col items-center">
                            <img
                                src={
                                    detailedUser.picture && detailedUser.picture.startsWith("https://")
                                        ? detailedUser.picture
                                        : `${fileUlr}/uploads/${detailedUser.picture || "default.png"}`
                                }
                                alt={detailedUser.name}
                                className="h-20 w-20 rounded-full object-cover border shadow-sm"
                            />
                            <h3 className="text-xl font-semibold mt-3">{detailedUser.name}</h3>
                            <p className="text-sm text-gray-500">@{detailedUser.username}</p>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-6">
                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                                <div className="text-lg font-bold">{totalPosts}</div>
                                <div className="text-xs text-gray-500">Posts</div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                                <div className="text-lg font-bold">{followersCount}</div>
                                <div className="text-xs text-gray-500">Followers</div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-lg text-center">
                                <div className="text-lg font-bold">{followingCount}</div>
                                <div className="text-xs text-gray-500">Following</div>
                            </div>
                        </div>

                        <div className="mt-5 space-y-2 text-sm text-gray-700">
                            <div>
                                <strong>Email:</strong> {detailedUser.email || "-"}
                            </div>
                            <div>
                                <strong>User Type:</strong> {detailedUser.userType || "-"}
                            </div>
                            <div>
                                <strong>Active since:</strong>{" "}
                                {detailedUser.activeDate ? tableDateFormate(detailedUser.activeDate) : "-"}
                            </div>
                            <div>
                                <strong>Deactivate date:</strong>{" "}
                                {detailedUser.deactivateDate ? tableDateFormate(detailedUser.deactivateDate) : "-"}
                            </div>

                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <div className="bg-gray-100 p-2 rounded text-center text-sm">
                                    Photos Left: <strong>{remainingPhotoLimit}</strong>
                                </div>
                                <div className="bg-gray-100 p-2 rounded text-center text-sm">
                                    Posts Left: <strong>{remainingPostLimit}</strong>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/* -----------------------------
   Subscription Manager Modal
   ----------------------------- */
const SubscriptionModal = ({ user, onClose, onUpdated }) => {
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState("");
    const [selectedPlan, setSelectedPlan] = useState("");
    const [loading, setLoading] = useState(false);
    const [packagesLoading, setPackagesLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/packages");
                const list = res.data?.packages || res.data || [];
                setPackages(Array.isArray(list) ? list : []);
            } catch (err) {
                console.error("Failed to load packages", err);
                toast.error("Could not load packages");
            } finally {
                setPackagesLoading(false);
            }
        })();
    }, []);

    const activePackage = packages.find((p) => p._id === selectedPackage);
    const availablePlans = activePackage?.plans?.filter((p) => p.isActive) || [];

    useEffect(() => {
        // Auto-select first plan when package changes
        if (availablePlans.length > 0) setSelectedPlan(availablePlans[0].planCode);
        else setSelectedPlan("");
    }, [selectedPackage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGrant = async () => {
        if (!selectedPackage || !selectedPlan) {
            toast.warn("Select a package and plan");
            return;
        }
        setLoading(true);
        try {
            await api.post(`/users/${user._id}/grant-subscription`, {
                packageId: selectedPackage,
                planCode: selectedPlan,
            });
            toast.success("Subscription granted");
            onUpdated?.();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Failed to grant subscription");
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async () => {
        if (!window.confirm(`Revoke subscription for ${user.name || user.username}?`)) return;
        setLoading(true);
        try {
            await api.post(`/users/${user._id}/revoke-subscription`);
            toast.success("Subscription revoked");
            onUpdated?.();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Failed to revoke subscription");
        } finally {
            setLoading(false);
        }
    };

    const isPremium = user.userType === "premium";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-black"
                    aria-label="Close"
                >
                    <X size={20} />
                </button>

                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-full bg-yellow-100">
                        <Crown size={22} className="text-yellow-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold">Manage Subscription</h3>
                        <p className="text-sm text-gray-500">
                            {user.name || user.username} · Current:{" "}
                            <span className={isPremium ? "text-green-600 font-medium" : "text-gray-500"}>
                                {user.userType || "free"}
                            </span>
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Package</label>
                        {packagesLoading ? (
                            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                        ) : (
                            <select
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none"
                                value={selectedPackage}
                                onChange={(e) => setSelectedPackage(e.target.value)}
                            >
                                <option value="">-- Select a package --</option>
                                {packages.map((p) => (
                                    <option key={p._id} value={p._id}>
                                        {p.name} {p.star ? `(${p.star}★)` : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {selectedPackage && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                            <select
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none"
                                value={selectedPlan}
                                onChange={(e) => setSelectedPlan(e.target.value)}
                                disabled={availablePlans.length === 0}
                            >
                                {availablePlans.length === 0 ? (
                                    <option>No active plans in this package</option>
                                ) : (
                                    availablePlans.map((p) => (
                                        <option key={p.planCode} value={p.planCode}>
                                            {p.name} — ₹{p.price} · {p.days} days
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    )}

                    {activePackage && (
                        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1">
                            <div>Daily posts: <strong>{activePackage.dailyPostLimit ?? 0}</strong></div>
                            <div>Daily photos: <strong>{activePackage.dailyPhotoLimit ?? 0}</strong></div>
                            {activePackage.features?.length > 0 && (
                                <div>Features: <em>{activePackage.features.join(", ")}</em></div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex gap-2 mt-6">
                    <button
                        onClick={handleGrant}
                        disabled={loading || !selectedPackage || !selectedPlan}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
                    >
                        <Crown size={16} />
                        {loading ? "Working..." : "Grant Subscription"}
                    </button>
                    <button
                        onClick={handleRevoke}
                        disabled={loading || !isPremium}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg font-medium hover:bg-red-100 disabled:opacity-50 transition"
                    >
                        <Ban size={16} />
                        Revoke
                    </button>
                </div>
            </div>
        </div>
    );
};

/* -----------------------------
   Filter bar component
   ----------------------------- */
const UserFilterBar = ({
    search,
    setSearch,
    userType,
    setUserType,
    activeStatus,
    setActiveStatus,
    activePackageId,
    setActivePackageId,
    packageOptions,
    limit,
    setLimit,
    onSearch,
}) => {
    return (
        <div className="bg-white border rounded-2xl shadow-sm p-5 mb-6 flex flex-wrap items-center gap-4">
            <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                    type="text"
                    placeholder="Search users (name / email)..."
                    className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl w-72 focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none transition"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onSearch()}
                />
            </div>

            <select
                className="px-4 py-2 border border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none transition"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
            >
                <option value="">User Type</option>
                <option value="trial">Trial</option>
                <option value="premium">Premium</option>
            </select>

            <select
                className="px-4 py-2 border border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none transition"
                value={activeStatus}
                onChange={(e) => setActiveStatus(e.target.value)}
            >
                <option value="">Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
            </select>

            <select
                className="px-4 py-2 border border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none transition"
                value={activePackageId}
                onChange={(e) => setActivePackageId(e.target.value)}
            >
                <option value="">All Packages</option>
                {(packageOptions || [])
                    .filter(p => (p.subscriberCount || 0) > 0)
                    .map(p => (
                        <option key={p._id} value={p._id}>
                            {p.name} ({p.subscriberCount})
                        </option>
                    ))}
            </select>

            <select
                className="px-4 py-2 border border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none transition"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
            >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
            </select>

            <div className="ml-auto flex gap-2">
                <button
                    onClick={() => {
                        // reset filters
                        setSearch("");
                        setUserType("");
                        setActiveStatus("");
                        setLimit(10);
                        onSearch();
                    }}
                    className="px-4 py-2 border rounded-xl hover:bg-gray-50"
                >
                    Reset
                </button>

                <button
                    onClick={onSearch}
                    className="px-5 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition shadow-sm"
                >
                    Apply
                </button>
            </div>
        </div>
    );
};

/* -----------------------------
   Table row component
   ----------------------------- */
const UserTableRow = ({
    item,
    index,
    page,
    isSmall,
    handleOpenImage,
    toggleStatus,
    deleteUser,
    openDetails,
    openSubscription,
    isSelected,
    onToggleSelect,
}) => {
    return (
        <tr className="border-b hover:bg-gray-50 transition">
            <td className="px-4 py-4">
                <input
                    type="checkbox"
                    checked={!!isSelected}
                    onChange={() => onToggleSelect?.(item._id)}
                    className="h-4 w-4 accent-purple-600 cursor-pointer"
                />
            </td>
            <td className="px-6 py-4 text-gray-700">{(page - 1) * 1 * 10 + index + 1}</td>

            <td
                className="px-6 py-4 font-medium flex items-center gap-3 cursor-pointer"
                onClick={() => openDetails(item)}
            >
                <img
                    src={
                        item.picture && item.picture.startsWith("https://")
                            ? item.picture
                            : `${fileUlr}/uploads/${item.picture || "default-user.png"}`
                    }
                    alt={item.name}
                    referrerPolicy="no-referrer"
                    className="h-8 w-8 rounded-full object-cover border"
                />
                <div>
                    <div className="text-sm">{item.name || "-"}</div>
                    <div className="text-xs text-gray-400">@{item.username}</div>
                </div>
            </td>

            <td className="px-6 py-4">
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 border">
                    {item.userType}
                </span>
            </td>

            <td className="px-6 py-4">
                <span className="text-sm text-gray-700">
                    {isSmall
                        ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                        })
                        : new Date(item.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                        })}
                </span>
            </td>

            <td className="px-6 py-4 flex gap-2 items-center">
                <button
                    onClick={() => handleOpenImage(item.picture)}
                    title="Open profile image"
                    className="p-2 rounded-lg hover:bg-purple-100 text-purple-600 transition"
                >
                    <Image size={16} />
                </button>

                <button
                    onClick={() => toggleStatus(item._id, !item.isActive)}
                    title={item.isActive ? "Deactivate" : "Activate"}
                    className="p-2 rounded-lg hover:bg-gray-200 text-gray-700 transition"
                >
                    {item.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>

                <button
                    onClick={() => openSubscription(item)}
                    title={item.userType === "premium" ? "Manage subscription" : "Grant subscription"}
                    className={`p-2 rounded-lg transition ${item.userType === "premium"
                        ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                        : "hover:bg-yellow-50 text-yellow-600"}`}
                >
                    <Crown size={16} />
                </button>

                <button
                    onClick={() => deleteUser(item._id)}
                    title="Delete user"
                    className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition"
                >
                    <Trash size={16} />
                </button>
            </td>
        </tr>
    );
};

/* -----------------------------
   Pagination component (top & bottom)
   improved UX controls
   ----------------------------- */
const PaginationBar = ({ page, totalPages, setPage, totalItems, className = "" }) => {
    return (
        <div className={`flex items-center gap-3 justify-between p-4 bg-white border ${className}`}>
            <div className="text-sm text-gray-600">
                Showing <strong>{page}</strong> of <strong>{totalPages}</strong> pages •{" "}
                <span className="text-gray-500">{totalItems ?? "-"} total</span>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    « First
                </button>

                <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    Prev
                </button>

                <div className="px-3 py-1 border rounded-lg bg-white text-sm">
                    {page}
                </div>

                <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    Next
                </button>

                <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    Last »
                </button>
            </div>
        </div>
    );
};

/* -----------------------------
   Main Page
   ----------------------------- */
export default function Users() {
    const { loading } = useContext(AuthContext);

    const isSmall = useIsSmall();

    const [usersData, setUsersData] = useState([]);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Original states
    const [search, setSearch] = useState("");
    const [userType, setUserType] = useState("");
    const [activeStatus, setActiveStatus] = useState("");
    const [activePackageId, setActivePackageId] = useState("");
    const [packageOptions, setPackageOptions] = useState([]);

    // Debounced versions
    const debouncedSearch = useDebounce(search, 1000);
    const debouncedUserType = useDebounce(userType, 1000);
    const debouncedActiveStatus = useDebounce(activeStatus, 1000);

    // Load package list with subscriber counts once.
    useEffect(() => {
        api.get('/packages/subscriber-counts')
            .then(res => setPackageOptions(res.data || []))
            .catch(() => {});
    }, []);

    // sorting
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState(""); // "asc" | "desc" | ""

    // modals
    const [selectedUser, setSelectedUser] = useState(null);
    const [subscriptionUser, setSubscriptionUser] = useState(null);

    // bulk selection
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkWorking, setBulkWorking] = useState(false);

    // fetch data
    const handelGetData = useCallback(async () => {
        try {
            const params = buildQueryParams({
                page,
                limit,
                search: debouncedSearch,
                userType: debouncedUserType,
                active: debouncedActiveStatus,
                activePackage: activePackageId || undefined,
                sortBy,
                sortOrder,
            });

            const response = await api.get("/users", { params });
            // expecting response.data: { page, limit, totalUserCount, totalPages, users }
            const d = response.data;
            setUsersData(d.users || []);
            setTotalPages(d.totalPages || 1);
            setTotalItems(d.totalUserCount ?? d.totalItems ?? 0);
        } catch (err) {
            console.error("fetch users error:", err);
            toast.error("Failed to load users");
        }
    }, [page, limit, debouncedSearch, debouncedUserType, debouncedActiveStatus, activePackageId, sortBy, sortOrder]);

    useEffect(() => {
        // reload when dependencies change
        handelGetData();
    }, [handelGetData, loading]);

    // sort toggle logic: asc -> desc -> none
    const handleSort = (col) => {
        if (sortBy !== col) {
            setSortBy(col);
            setSortOrder("asc");
        } else {
            if (sortOrder === "asc") setSortOrder("desc");
            else if (sortOrder === "desc") {
                setSortBy("");
                setSortOrder("");
            } else setSortOrder("asc");
        }
        // reset to first page when sorting changes
        setPage(1);
    };

    const deleteUser = async (id) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            const res = await api.delete(`/users/${id}`);
            if (res.status === 200) {
                toast.success("User deleted");
                handelGetData();
            }
        } catch (err) {
            console.error(err);
            toast.error("Delete failed");
        }
    };

    const toggleStatus = async (id, status) => {
        try {
            const res = await api.patch(`/users/active/${id}`, { isActive: status });
            if (res.status === 200) {
                toast.success("Status updated");
                handelGetData();
            }
        } catch (err) {
            console.error(err);
            toast.error("Status update failed");
        }
    };

    const handleOpenImage = (picture) => {
        if (!picture) return;
        if (picture.startsWith("https://")) window.open(picture, "_blank");
        else window.open(`${fileUlr}/uploads/${picture}`, "_blank");
    };

    // when filters change, reset page to 1
    useEffect(() => {
        setPage(1);
    }, [limit, userType, activeStatus, search, sortBy, sortOrder]);

    // Clear selection whenever the page/query changes (ids no longer visible)
    useEffect(() => {
        setSelectedIds(new Set());
    }, [page, limit, debouncedSearch, debouncedUserType, debouncedActiveStatus, sortBy, sortOrder]);

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAllOnPage = () => {
        const allIds = usersData.map((u) => u._id);
        const allSelected = allIds.every((id) => selectedIds.has(id));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                allIds.forEach((id) => next.delete(id));
            } else {
                allIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const handleBulkSuspend = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        setBulkWorking(true);
        try {
            await api.patch(`/users/bulk-suspend`, { ids });
            toast.success(`Suspended ${ids.length} user(s)`);
            setSelectedIds(new Set());
            handelGetData();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Bulk suspend failed");
        } finally {
            setBulkWorking(false);
        }
    };

    const handleBulkActivate = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        setBulkWorking(true);
        try {
            await api.patch(`/users/bulk-activate`, { ids });
            toast.success(`Activated ${ids.length} user(s)`);
            setSelectedIds(new Set());
            handelGetData();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Bulk activate failed");
        } finally {
            setBulkWorking(false);
        }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} user(s)? This cannot be undone.`)) return;
        setBulkWorking(true);
        try {
            await api.delete(`/users/bulk`, { data: { ids } });
            toast.success(`Deleted ${ids.length} user(s)`);
            setSelectedIds(new Set());
            handelGetData();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Bulk delete failed");
        } finally {
            setBulkWorking(false);
        }
    };

    const buildExportRows = (list) => list.map((u) => ({
        _id: u._id,
        name: u.name,
        email: u.email,
        phone: u.phone || u.mobile || '',
        isActive: u.isActive,
        userType: u.userType,
        createdAt: u.createdAt,
        'subscription.package': u.subscription?.package?.name || u.subscription?.package || '',
        'subscription.expiresAt': u.subscription?.expiresAt || '',
    }));
    const exportHeaders = ["_id", "name", "email", "phone", "isActive", "userType", "createdAt", "subscription.package", "subscription.expiresAt"];

    const handleExportCSV = () => {
        if (!usersData || usersData.length === 0) {
            toast.info("Nothing to export on this page");
            return;
        }
        download(toCSV(buildExportRows(usersData), exportHeaders), `users-page-${page}.csv`);
    };

    // Export every user across all pages by paginating through the API.
    const handleExportAll = async () => {
        try {
            toast.info("Preparing full export…");
            const all = [];
            const params = new URLSearchParams();
            params.set("limit", "200");
            if (debouncedSearch) params.set("search", debouncedSearch);
            if (debouncedUserType) params.set("userType", debouncedUserType);
            if (debouncedActiveStatus) params.set("active", debouncedActiveStatus);
            if (sortBy) { params.set("sortBy", sortBy); params.set("sortOrder", sortOrder); }
            for (let p = 1; p <= 50; p++) {
                params.set("page", String(p));
                const res = await api.get(`/users?${params.toString()}`);
                const rows = res.data?.users || res.data?.data || [];
                if (!rows.length) break;
                all.push(...rows);
                if (rows.length < 200) break;
            }
            if (all.length === 0) {
                toast.info("No users to export");
                return;
            }
            download(toCSV(buildExportRows(all), exportHeaders), `users-all-${new Date().toISOString().slice(0,10)}.csv`);
            toast.success(`Exported ${all.length} users`);
        } catch (err) {
            console.error(err);
            toast.error("Export all failed");
        }
    };

    const allOnPageSelected = usersData.length > 0 && usersData.every((u) => selectedIds.has(u._id));
    const someOnPageSelected = usersData.some((u) => selectedIds.has(u._id));

    return (
        <MainLayout title="Users"
            Buttons={
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExportCSV}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md text-sm"
                    >
                        <Download size={16} />
                        Export Page
                    </button>
                    <button
                        onClick={handleExportAll}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm"
                    >
                        <Download size={16} />
                        Export All
                    </button>
                </div>
            }
        >
            {/* Top Pagination */}
            <div className="mb-4">
                <PaginationBar
                    page={page}
                    totalPages={totalPages}
                    setPage={setPage}
                    totalItems={totalItems}
                    className="rounded-t-2xl"
                />
            </div>

            {/* Filters */}
            <UserFilterBar
                search={search}
                setSearch={setSearch}
                userType={userType}
                setUserType={setUserType}
                activeStatus={activeStatus}
                setActiveStatus={setActiveStatus}
                activePackageId={activePackageId}
                setActivePackageId={setActivePackageId}
                packageOptions={packageOptions}
                limit={limit}
                setLimit={setLimit}
                onSearch={() => {
                    setPage(1);
                    handelGetData();
                }}
            />

            {/* Bulk action bar — sticky near top when selections exist */}
            {selectedIds.size > 0 && (
                <div className="sticky top-16 z-20 mb-4 flex items-center justify-between gap-3 bg-purple-600 text-white rounded-xl shadow px-4 py-3">
                    <div className="text-sm font-medium">
                        {selectedIds.size} user{selectedIds.size === 1 ? "" : "s"} selected
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleBulkActivate}
                            disabled={bulkWorking}
                            className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-sm disabled:opacity-50"
                        >
                            <UserCheck size={14} /> Activate
                        </button>
                        <button
                            onClick={handleBulkSuspend}
                            disabled={bulkWorking}
                            className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-sm disabled:opacity-50"
                        >
                            <UserX size={14} /> Suspend
                        </button>
                        <button
                            onClick={handleBulkDelete}
                            disabled={bulkWorking}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 rounded-md text-sm disabled:opacity-50"
                        >
                            <Trash size={14} /> Delete
                        </button>
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            disabled={bulkWorking}
                            className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-sm disabled:opacity-50"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {/* Table Card */}
            <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold sticky top-0 z-10">
                        <tr>
                            <th className="px-4 py-4">
                                <input
                                    type="checkbox"
                                    checked={allOnPageSelected}
                                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                                    onChange={toggleSelectAllOnPage}
                                    className="h-4 w-4 accent-purple-600 cursor-pointer"
                                />
                            </th>
                            <th className="px-6 py-4">#</th>

                            <th
                                className="px-6 py-4 cursor-pointer select-none"
                                onClick={() => handleSort("name")}
                            >
                                <div className="flex items-center">
                                    Name
                                    <SortIcon active={sortBy === "name"} order={sortOrder} />
                                </div>
                            </th>

                            <th
                                className="px-6 py-4 cursor-pointer select-none"
                                onClick={() => handleSort("userType")}
                            >
                                <div className="flex items-center">
                                    Type
                                    <SortIcon active={sortBy === "userType"} order={sortOrder} />
                                </div>
                            </th>

                            <th
                                className="px-6 py-4 cursor-pointer select-none"
                                onClick={() => handleSort("createdAt")}
                            >
                                <div className="flex items-center">
                                    Created
                                    <SortIcon active={sortBy === "createdAt"} order={sortOrder} />
                                </div>
                            </th>

                            <th className="px-6 py-4">Action</th>
                        </tr>
                    </thead>

                    <tbody className="text-gray-700">
                        {usersData.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                                    No users found
                                </td>
                            </tr>
                        ) : (
                            usersData.map((item, idx) => (
                                <UserTableRow
                                    key={item._id || idx}
                                    item={item}
                                    index={idx}
                                    page={page}
                                    isSmall={isSmall}
                                    handleOpenImage={handleOpenImage}
                                    toggleStatus={toggleStatus}
                                    deleteUser={deleteUser}
                                    openDetails={(u) => setSelectedUser(u)}
                                    openSubscription={(u) => setSubscriptionUser(u)}
                                    isSelected={selectedIds.has(item._id)}
                                    onToggleSelect={toggleSelectOne}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Bottom Pagination */}
            <div className="mt-4">
                <PaginationBar
                    page={page}
                    totalPages={totalPages}
                    setPage={setPage}
                    totalItems={totalItems}
                    className="rounded-b-2xl"
                />
            </div>

            {/* User details modal */}
            {selectedUser && (
                <UserMoreDetails user={selectedUser} onClose={() => setSelectedUser(null)} />
            )}

            {/* Subscription management modal */}
            {subscriptionUser && (
                <SubscriptionModal
                    user={subscriptionUser}
                    onClose={() => setSubscriptionUser(null)}
                    onUpdated={handelGetData}
                />
            )}
        </MainLayout>
    );
}
